import { validateTrackingUrl } from '@b-stock/fe-core/bstock-next/helpers'

function isObjectRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null
}

/**
 * Payload Lexical `link` / `autolink` nodes store the target in `fields.url` for **custom**
 * links. For **internal** links, `exportJSON` omits `url`; when the REST API populates
 * `fields.doc.value`, the resolved URL may live on that object (shape varies by collection).
 */
export function extractLexicalLinkUrl(fields: unknown): string {
  if (!isObjectRecord(fields)) return ''
  const direct = typeof fields.url === 'string' ? fields.url.trim() : ''
  if (direct) return direct
  if (fields.linkType !== 'internal') return ''
  if (!isObjectRecord(fields.doc)) return ''
  const value = fields.doc.value
  if (!isObjectRecord(value)) return ''
  for (const key of ['url', 'href', 'linkUrl', 'fullPath'] as const) {
    const v = value[key]
    if (typeof v === 'string' && v.trim()) return v.trim()
  }
  return ''
}

/**
 * Same-origin relative signup URLs used in legacy JSX (`/acct/signup?…`).
 * Protocol-relative (`//…`) is rejected.
 */
function isAllowedRelativeSignupHref(trimmed: string): boolean {
  return (
    trimmed === '/acct/signup' ||
    trimmed.startsWith('/acct/signup?') ||
    trimmed.startsWith('/acct/signup/')
  )
}

/**
 * Resolve Lexical `link` / `autolink` `href` for seller profile rich text.
 * - `https?://…` URLs must pass {@link validateTrackingUrl}
 * - Legacy parity: relative `/acct/signup…` on the storefront origin is returned as-is
 */
export function resolveSellerProfileLexicalHref(raw: string): string | null {
  const trimmed = raw.trim()
  if (!trimmed) return null
  const validated = validateTrackingUrl(trimmed)
  if (validated) return validated
  if (trimmed.startsWith('//')) return null
  if (trimmed.startsWith('/') && isAllowedRelativeSignupHref(trimmed)) {
    return trimmed
  }
  return null
}

export function isSignupAccountHref(href: string): boolean {
  const t = href.trim()
  if (!t) return false
  try {
    if (t.startsWith('/') && !t.startsWith('//')) {
      return isAllowedRelativeSignupHref(t)
    }
    const u = new URL(t)
    return (
      u.pathname === '/acct/signup' || u.pathname.startsWith('/acct/signup/')
    )
  } catch {
    return false
  }
}

type SignupTrackingTab =
  | 'about'
  | 'faq'
  | 'condition'
  | 'shipping'
  | 'device-unlocking-terms'

/** GTM `cta_location`, e.g. `costco_about` / `costco_faq` for slug `costco`. */
export function signUpCtaLocationForTab(
  sellerSlug: string,
  tab: SignupTrackingTab
): string {
  return `${sellerSlug}_${tab}`
}
